/**
 * CopilotIdentitiesPanel — app-level Settings UI for named GitHub Copilot CLI
 * identities. Parallel to ClaudeIdentitiesPanel, but each identity is an
 * isolated `COPILOT_HOME` (its own `copilot login` + sessions), so a personal
 * account and a work account can coexist permanently, scoped per project.
 *
 * This panel lists identities, lets you add/edit/delete them, pick the global
 * default, and shows whether each home dir has been set up. "Log in" opens a
 * terminal scoped to the identity's COPILOT_HOME running `copilot` so you can
 * complete the interactive GitHub auth. Copilot keeps its token outside a
 * readable file, so status is "set up / not set up", not an account email.
 *
 * Backing surface: `backend.copilotIdentity.*` → core `copilotIdentity.*`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Github, Plus, Trash2, Star, LogIn, CheckCircle2, XCircle } from 'lucide-react';
import { useBackend } from '@/services/backend';
import type { CopilotIdentity, CopilotAuthStatus } from '@/services/backend/IBackend';
import { logger } from '@/lib/logger';
import { useProjectStore } from '@/stores/projectStore';
import { useTerminalStore } from '@/stores/terminalStore';
import { useUIStore } from '@/stores/uiStore';

const TAG = 'CopilotIdentitiesPanel';

interface IdentityForm {
  /** the identity being edited, or '' when adding a new one */
  original: string;
  name: string;
  homeDir: string;
  account: string;
}

const EMPTY_FORM: IdentityForm = { original: '', name: '', homeDir: '', account: '' };

export default function CopilotIdentitiesPanel() {
  const backend = useBackend();
  const currentProject = useProjectStore((s) => s.currentProject);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [identities, setIdentities] = useState<CopilotIdentity[]>([]);
  const [statuses, setStatuses] = useState<Record<string, CopilotAuthStatus>>({});
  const [form, setForm] = useState<IdentityForm | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await backend.copilotIdentity.list();
      setIdentities(list);
      const entries = await Promise.all(
        list.map(async (i) => {
          try {
            return [i.name, await backend.copilotIdentity.status(i.homeDir)] as const;
          } catch {
            return [i.name, { initialized: false }] as const;
          }
        }),
      );
      setStatuses(Object.fromEntries(entries));
    } catch (e) {
      logger.warn(TAG, 'load failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [backend]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const saveForm = useCallback(async () => {
    if (!form) return;
    const name = form.name.trim();
    const homeDir = form.homeDir.trim();
    if (!name || !homeDir) {
      setError('Name and home dir are required.');
      return;
    }
    setBusy('save');
    try {
      if (form.original && form.original !== name) {
        await backend.copilotIdentity.remove(form.original);
      }
      await backend.copilotIdentity.upsert({ name, homeDir, account: form.account.trim() || undefined });
      setForm(null);
      await load();
    } catch (e) {
      logger.warn(TAG, 'save failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [backend, form, load]);

  const remove = useCallback(async (name: string) => {
    setBusy(name);
    try {
      await backend.copilotIdentity.remove(name);
      await load();
    } catch (e) {
      logger.warn(TAG, 'delete failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [backend, load]);

  const setDefault = useCallback(async (name: string) => {
    setBusy(name);
    try {
      await backend.copilotIdentity.setDefault(name);
      await load();
    } catch (e) {
      logger.warn(TAG, 'setDefault failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [backend, load]);

  /**
   * Open a terminal scoped to `identity.homeDir` (via the COPILOT_HOME env
   * override that core's resolution treats as highest-precedence) and run
   * `copilot` so the user can complete the interactive GitHub login for that
   * identity. Uses the same pending-command → TerminalTab path as the Claude
   * panel, then switches to the terminal tab and closes Settings.
   */
  const onAuthenticate = useCallback((identity: CopilotIdentity) => {
    if (!currentProject) {
      setError('Open a project first — the login terminal opens in the active project.');
      return;
    }
    useTerminalStore.getState().setPendingCommand({
      command: 'copilot',
      title: `Copilot login — ${identity.name}`,
      projectPath: currentProject.root_path,
      env: { COPILOT_HOME: identity.homeDir },
      kind: 'auth',
    });
    useTerminalStore.getState().requestTabSwitch();
    useUIStore.getState().closeSettings();
  }, [currentProject]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><span className="loading loading-spinner loading-md" /></div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Github className="w-4 h-4 text-primary" />
          <h3 className="font-medium">Copilot Identities</h3>
        </div>
        <p className="text-sm opacity-70">
          Each identity is an isolated <code className="text-xs">COPILOT_HOME</code> — its own
          GitHub Copilot login and sessions. Pick a default for all projects; a project overrides it
          via <code className="text-xs"> copilotIdentity</code> in its config. Terminals (and the
          Copilot CLI launched in them) run under the resolved identity automatically.
        </p>
      </div>

      {error && (
        <div className="alert alert-warning text-sm py-2">
          <XCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {identities.length === 0 && (
        <div className="text-sm opacity-60 border border-dashed border-base-300 rounded-lg p-4 text-center">
          No identities yet. Add one to isolate a work Copilot login from your personal account.
        </div>
      )}

      <div className="space-y-2">
        {identities.map((id) => {
          const st = statuses[id.name];
          return (
            <div key={id.name} className="border border-base-300 rounded-lg p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{id.name}</span>
                  {id.isDefault && (
                    <span className="badge badge-sm badge-primary gap-1"><Star className="w-3 h-3" /> default</span>
                  )}
                  {st?.initialized ? (
                    <span className="badge badge-sm badge-success gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Set up
                    </span>
                  ) : (
                    <span className="badge badge-sm badge-ghost gap-1">
                      <XCircle className="w-3 h-3" /> Not set up
                    </span>
                  )}
                </div>
                <div className="text-xs opacity-60 truncate mt-0.5 font-mono">{id.homeDir}</div>
                {id.account && <div className="text-xs opacity-60 truncate">{id.account}</div>}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button className="btn btn-ghost btn-xs gap-1" disabled={busy === id.name} onClick={() => onAuthenticate(id)} title="Log in to this identity (runs `copilot` with its COPILOT_HOME)">
                  <LogIn className="w-3.5 h-3.5" /> {st?.initialized ? 'Re-login' : 'Log in'}
                </button>
                {!id.isDefault && (
                  <button className="btn btn-ghost btn-xs gap-1" disabled={busy === id.name} onClick={() => setDefault(id.name)} title="Make this the default identity">
                    <Star className="w-3.5 h-3.5" /> Default
                  </button>
                )}
                <button
                  className="btn btn-ghost btn-xs"
                  disabled={busy === id.name}
                  onClick={() => setForm({ original: id.name, name: id.name, homeDir: id.homeDir, account: id.account ?? '' })}
                  title="Edit"
                >
                  Edit
                </button>
                <button className="btn btn-ghost btn-xs text-error" disabled={busy === id.name} onClick={() => remove(id.name)} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {form ? (
        <div className="border border-base-300 rounded-lg p-3 space-y-3">
          <div className="font-medium text-sm">{form.original ? `Edit "${form.original}"` : 'Add identity'}</div>
          <div className="grid grid-cols-1 gap-2">
            <label className="form-control">
              <span className="label-text text-xs mb-1">Name</span>
              <input
                className="input input-bordered input-sm"
                placeholder="work-advantage"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text text-xs mb-1">Home dir (COPILOT_HOME)</span>
              <input
                className="input input-bordered input-sm font-mono"
                placeholder={form.name ? `~/.copilot-${form.name}` : '~/.copilot-work'}
                value={form.homeDir}
                onChange={(e) => setForm({ ...form, homeDir: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text text-xs mb-1">Account label (optional — e.g. your GitHub login)</span>
              <input
                className="input input-bordered input-sm"
                placeholder="octocat (personal) / acme-corp (work)"
                value={form.account}
                onChange={(e) => setForm({ ...form, account: e.target.value })}
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-primary btn-sm" disabled={busy === 'save'} onClick={saveForm}>
              {busy === 'save' ? <span className="loading loading-spinner loading-xs" /> : 'Save'}
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy === 'save'} onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-outline btn-sm gap-1" onClick={() => setForm({ ...EMPTY_FORM })}>
          <Plus className="w-4 h-4" /> Add identity
        </button>
      )}
    </div>
  );
}
